import React, { useEffect } from "react";
import { Button, Form, Input, Modal } from "antd";
import styles from "./NeurapulseConfigDialog.module.css";
import { FormComponentProps } from "antd/lib/form";
import { useApi } from "src/Api/useApi";
import {
	NeurapulseConfiguration,
	NeurapulseTestApiKey,
	ProblemDetails,
} from "src/@types/ImpiricaPlatformApi";
import clsx from "clsx";
import { NeurapulseConfigDialogHeader } from "./NeurapulseConfigDialogHeader";
import { NeurapulseProjectDisplay } from "./NeurapulseProjectDisplay";
import { NeurapulseProjectSelector } from "./NeurapulseProjectSelector";
import { NeurapulseClientImport } from "./NeurapulseClientImport";

type NeurapulseConfigDialogProps = {
	onClose: (update: boolean) => void;
	visible: boolean;
	dataset: DatasetDTO;
};

export const NeurapulseConfigDialog = Form.create<
	FormComponentProps & NeurapulseConfigDialogProps
>({ name: "neurapulse_config_dialog" })(
	(props: FormComponentProps & NeurapulseConfigDialogProps) => {
		const EMPTY_KEY = "********************************";

		useEffect(() => {
			// Reset validation when dialog is opened
			if (props.visible) {
				setValidated(false);
			}
			// Reset inputs when dialog is closed
			else {
				props.form.resetFields();
			}
		}, [props.visible]);

		const { neurapulseClient } = useApi();
		if (!neurapulseClient) throw new Error("Api is not available");
		const [saving, setSaving] = React.useState(false);
		const [testing, setTesting] = React.useState(false);
		const [validated, setValidated] = React.useState(false);
		const { form, visible, onClose, dataset } = props;
		const {
			getFieldDecorator,
			validateFields,
			resetFields,
			getFieldValue,
			getFieldError,
			isFieldTouched,
			setFields,
		} = form;

		if (!dataset || !dataset.id || !dataset.organizationId) {
			console.error("Dataset information is incomplete.");
			return null;
		}

		const apiKeyValue = getFieldValue("apiKey");
		const apiKeyErrors = getFieldError("apiKey");
		const apiKeyTouched = isFieldTouched("apiKey");
		const projectIdValue = getFieldValue("projectId");
		const apiKeyEmpty = !apiKeyValue || !/[^*]/.test(apiKeyValue); // not replaced yet (all asterisks)

		const testButtonDisabled =
			apiKeyEmpty || !apiKeyTouched || saving || testing || validated;

		const validateStatus = React.useMemo(() => {
			if (apiKeyErrors && apiKeyErrors.length > 0) return "error";
			if (validated) return "success";
			return undefined;
		}, [apiKeyErrors, validated]);

		const okButtonDisabled = saving || !validated;

		const handleApiKeyInvalid = () => {
			setFields({
				apiKey: {
					value: apiKeyValue,
					errors: [
						new Error("The API Key appears to be invalid. Please try again."),
					],
				},
			});
			setValidated(false);
		};

		const handleApiKeyValid = () => {
			setFields({
				apiKey: {
					value: apiKeyValue,
					errors: [],
				},
			});
			setValidated(true);
		};

		const handleTestApiKey = async () => {
			// Only validate the apiKey field so required project selection doesn't block testing
			validateFields(["apiKey"], async (err) => {
				if (err) return; // apiKey has validation errors
				setTesting(true);
				try {
					if (!dataset || !dataset.organizationId || !dataset.id) {
						throw new Error("Dataset information is incomplete.");
					}
					await neurapulseClient.testNeurapulseApiKey(
						new NeurapulseTestApiKey({
							organizationId: dataset.organizationId,
							datasetId: dataset.id,
							apiKey: apiKeyValue,
						})
					);
					handleApiKeyValid();
				} catch (error) {
					handleApiKeyInvalid();
				}
				setTesting(false);
			});
		};

		const handleApiKeyChange = () => {
			if (validated) setValidated(false);
		};

		const handleApiKeyFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			e.target.select();
		};

		const handleOk = async () => {
			validateFields(async (err) => {
				if (!err) {
					let errors = false;
					setSaving(true);
					try {
						await neurapulseClient.saveNeurapulseConfiguration(
							dataset.organizationId!,
							dataset.id!,
							new NeurapulseConfiguration({
								apiKey: apiKeyValue,
								projectId: projectIdValue === "" ? null : projectIdValue,
							})
						);
					} catch (error) {
						errors = true;
						if (error instanceof ProblemDetails) {
							handleApiKeyInvalid();
						}
					}
					setSaving(false);
					if (!errors) {
						onClose(true);
					}
				}
			});
		};

		return (
			<Modal
				title={<NeurapulseConfigDialogHeader />}
				visible={visible}
				onCancel={() => {
					resetFields();
					setValidated(false);
					onClose(false);
				}}
				onOk={handleOk}
				width={680}
				okText={
					saving ? (
						<span>
							<span className={clsx(styles.spinner)} /> Saving...
						</span>
					) : (
						"OK"
					)
				}
				okButtonProps={{ disabled: okButtonDisabled }}
			>
				<Form layout="vertical">
					<Form.Item
						label="Neurapulse Api Key"
						validateStatus={validateStatus}
						hasFeedback={!!validateStatus}
					>
						{getFieldDecorator("apiKey", {
							initialValue:
								props.dataset.neurapulseApiKey === "" ? EMPTY_KEY : "",
							rules: [
								{
									required: true,
									message: "Please input your Neurapulse API Key",
								},
							],
						})(
							<Input
								className={clsx(styles.apiKeyInput)}
								placeholder="Enter your Neurapulse API Key"
								onChange={handleApiKeyChange}
								onFocus={handleApiKeyFocus}
							/>
						)}

						<Button
							onClick={handleTestApiKey}
							disabled={testButtonDisabled}
							htmlType="button"
							type="primary"
							className={clsx(styles.testButton)}
						>
							Test
						</Button>
					</Form.Item>

					{/* if the neurapulseProjectId prop is already set, then render the static component */}
					<Form.Item label="Neurapulse Project">
						{getFieldDecorator("projectId", {
							initialValue: props.dataset.neurapulseProjectId || "",
						})(
							props.dataset.neurapulseProjectId ? (
								<NeurapulseProjectDisplay
									organizationId={props.dataset.organizationId}
									datasetId={props.dataset.id}
								/>
							) : validated ? (
								<NeurapulseProjectSelector apiKey={apiKeyValue} />
							) : (
								<span className={styles.selectorPlaceholder}>
									Enter a valid Api key.
								</span>
							)
						)}
					</Form.Item>
				</Form>
				{dataset.neurapulseProjectId && (
					<NeurapulseClientImport
						datasetId={dataset.id!}
						organizationId={dataset.organizationId!}
						onRefreshComplete={() => onClose(true)}
					/>
				)}
			</Modal>
		);
	}
);
